import type {
  ContentItemLinkDetails,
  ContentItemPreview,
  ImportContentPayload,
  ImportContentPayloadServer,
  ImportContentResult,
  UploadAssetPayload,
  UploadAssetPayloadServer,
  UploadAssetResult,
} from '@metapic-apps/shared'
import type { AxiosRequestConfig } from 'axios'
import { type Ref, ref, watch } from 'vue'

import { handleContentItemFilter } from '@/lib/adapters/content-items.adapter'
import { notification } from '@/lib/helpers'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { socialMediaClient } from '@/lib/services/social-media.service'
import { ContentItemStatusActionEnum, ContentItemStatusEnum } from '@/lib/types/content-item.types'
import type { ContentItemsFilterType } from '@/lib/types/content-items.types'
import { useErrorStore } from '@/stores/error'

type UseGetContentItemsReturn = {
  data: Ref<ContentItemPreview[]>
  totalPages: Ref<number>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  refetch: () => Promise<void>
}

const {
  listContentItems,
  changeContentStatus,
  getLinkDetails,
  uploadAsset,
  importContent,
  assignCampaign,
  removeCampaign,
} = socialMediaClient

const uploadAssetWithBlob = uploadAsset as unknown as (
  payload: UploadAssetPayload,
  options?: AxiosRequestConfig,
) => Promise<UploadAssetResult>

export const useGetContentItems = (
  filterRef: Ref<ContentItemsFilterType>,
): UseGetContentItemsReturn => {
  const data = ref<ContentItemPreview[]>([])
  const totalPages = ref<number>(0)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchContentItems = async (filter: ContentItemsFilterType) => {
    try {
      data.value = []
      isLoading.value = true
      error.value = null

      const result = await listContentItems(handleContentItemFilter({ ...filter, limit: 20 }))
      data.value = result.items
      totalPages.value = result.meta.totalPages
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  const refetch = async () => {
    await fetchContentItems(filterRef.value)
  }

  watch(
    filterRef,
    async () => {
      await refetch()
    },
    { immediate: true, deep: true },
  )

  return {
    data,
    totalPages,
    isLoading,
    error,
    refetch,
  }
}

export const useChangeContentItemStatus = () => {
  const isLoading = ref(false)
  const errorStore = useErrorStore()

  const changeContentItemStatus = async (
    contentItemId: string,
    action: ContentItemStatusActionEnum,
  ): Promise<boolean> => {
    try {
      isLoading.value = true
      await changeContentStatus(contentItemId, { action })
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      return false
    } finally {
      isLoading.value = false
    }
  }

  return { changeContentItemStatus, isLoading }
}

export const useHandleContentItemStatus = (
  getItems: () => { id: string; status: string }[],
  onSuccess?: (id: string, newStatus: ContentItemStatusEnum) => void,
) => {
  const { changeContentItemStatus, isLoading } = useChangeContentItemStatus()
  const currentAction = ref<ContentItemStatusActionEnum | null>(null)

  const handleStatusChange = async (id: string, action: ContentItemStatusActionEnum) => {
    currentAction.value = action
    const success = await changeContentItemStatus(id, action)
    if (success) {
      const newStatus =
        action === ContentItemStatusActionEnum.Approve
          ? ContentItemStatusEnum.Approved
          : ContentItemStatusEnum.Rejected
      const item = getItems().find((item) => item.id === id)
      if (item) {
        item.status = newStatus
      }
      notification.success('Content item status updated successfully.')
      onSuccess?.(id, newStatus)
    }
    currentAction.value = null
    return success
  }

  return { handleStatusChange, isLoading, currentAction }
}

export const useUploadContentItem = () => {
  const data = ref<UploadAssetResult | null>(null)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const uploadContentItem = async ({
    platform,
    handle,
    affiliateLinkId,
    advertiserId,
    asset,
  }: UploadAssetPayload): Promise<UploadAssetResult | null> => {
    try {
      isLoading.value = true
      error.value = null
      data.value = null

      const payload: UploadAssetPayloadServer = {
        platform,
        handle,
        asset,
        ...(affiliateLinkId ? { affiliate_link_id: affiliateLinkId } : {}),
        ...(advertiserId ? { advertiser_id: advertiserId } : {}),
      }

      const result = await uploadAssetWithBlob(payload, { isFormData: true })
      data.value = result

      return result
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error

      return null
    } finally {
      isLoading.value = false
    }
  }

  return { data, uploadContentItem, isLoading, error }
}

const MAX_IMPORT_POLL_ATTEMPTS = 20
const IMPORT_POLL_INTERVAL_MS = 1000

export const useImportContentItem = () => {
  const data = ref<ImportContentResult | null>(null)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const importContentItem = async ({
    contentType,
    url,
    advertiserId,
    campaignId,
  }: ImportContentPayload): Promise<ImportContentResult | null> => {
    try {
      isLoading.value = true
      error.value = null
      data.value = null

      const payload: ImportContentPayloadServer = {
        content_type: contentType,
        url,
        advertiser_id: advertiserId,
        campaign_id: campaignId,
      }

      let result = await importContent(payload)
      let attempts = 1
      while (result.status === 'running' && attempts < MAX_IMPORT_POLL_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, IMPORT_POLL_INTERVAL_MS))
        result = await importContent(payload)
        attempts += 1
      }
      data.value = result

      return result
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error

      return null
    } finally {
      isLoading.value = false
    }
  }

  return { data, importContentItem, isLoading, error }
}

export const useGetContentItemDetails = (idRef: Ref<string | null>) => {
  const data = ref<ContentItemLinkDetails | null>(null)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchDetails = async (id: string) => {
    try {
      isLoading.value = true
      error.value = null
      const result = await getLinkDetails(id)
      data.value = result
    } catch (e: unknown) {
      const parsedError = e as Error
      errorStore.setError(handleErrorMessage(e))
      error.value = parsedError
    } finally {
      isLoading.value = false
    }
  }

  watch(
    idRef,
    (id) => {
      if (id) {
        void fetchDetails(id)
      }
    },
    { immediate: true },
  )

  const refetch = async () => {
    if (idRef.value) {
      await fetchDetails(idRef.value)
    }
  }

  return { data, isLoading, error, refetch }
}

export const useAssignContentItemCampaign = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const assignContentItemCampaign = async (contentItemId: string, campaignId: number) => {
    try {
      isLoading.value = true
      error.value = null
      await assignCampaign(contentItemId, { campaign_id: campaignId })
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return { assignContentItemCampaign, isLoading, error }
}

export const useRemoveContentItemCampaign = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const removeContentItemCampaign = async (contentItemId: string, campaignId: number) => {
    try {
      isLoading.value = true
      error.value = null
      await removeCampaign(contentItemId, campaignId)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return { removeContentItemCampaign, isLoading, error }
}
